import React, { useState } from 'react';
import { AlertCircle, Ruler as RulerIcon } from 'lucide-react';
import { ImportSummary, ErrorSummary } from './types';
import { TAMANHO_COLORS } from './StatsCard';

type FilterTab = 'all' | 'valid' | 'invalid';

type ImportPreviewTableProps = {
  totalRows: number;
  validCount: number;
  invalidCount: number;
  tamanhoSummary: ImportSummary[];
  errorSummary: ErrorSummary[];
};

const defaultColor = {
  iconBg: 'bg-gray-100',
  iconText: 'text-gray-500',
  barBg: 'bg-gray-100',
  barFill: 'bg-gray-400',
};

export const ImportPreviewTable: React.FC<ImportPreviewTableProps> = ({
  totalRows,
  validCount,
  invalidCount,
  tamanhoSummary,
  errorSummary,
}) => {
  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');

  const tabs: { key: FilterTab; label: string; count: number }[] = [
    { key: 'all', label: 'Todos', count: totalRows },
    { key: 'valid', label: 'Válidos', count: validCount },
    { key: 'invalid', label: 'Inválidos', count: invalidCount },
  ];

  return (
    <div className="flex flex-col gap-3 min-h-0">
      <div className="flex flex-wrap gap-1.5 flex-shrink-0" role="tablist" aria-label="Filtrar itens da importação">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            role="tab"
            aria-selected={activeFilter === tab.key}
            onClick={() => setActiveFilter(tab.key)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              activeFilter === tab.key
                ? 'bg-gray-900 text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.label}
            <span
              className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
                activeFilter === tab.key
                  ? 'bg-white/20 text-white'
                  : 'bg-gray-200 text-gray-600'
              }`}
            >
              {tab.count.toLocaleString('pt-BR')}
            </span>
          </button>
        ))}
      </div>

      <div className="border border-gray-200 rounded-xl overflow-hidden min-h-[120px] max-h-[35vh] overflow-y-auto">
        {activeFilter !== 'invalid' && tamanhoSummary.length > 0 && (
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50 sticky top-0 z-10 shadow-[0_1px_0_0_theme(colors.gray.200)]">
              <tr>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-500 uppercase tracking-wider">
                  Tamanho UL
                </th>
                <th className="px-3 py-2.5 text-right font-semibold text-gray-500 uppercase tracking-wider">
                  CNPJs
                </th>
                <th className="px-3 py-2.5 text-right font-semibold text-gray-500 uppercase tracking-wider">
                  %
                </th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-500 uppercase tracking-wider w-32">
                  Distribuição
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tamanhoSummary.map((item) => {
                const pct = validCount > 0 ? (item.count / validCount) * 100 : 0;
                const color = TAMANHO_COLORS[item.tamanhoUL] ?? defaultColor;
                return (
                  <tr key={item.tamanhoUL} className="bg-white hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className={`${color.iconBg} ${color.iconText} p-1 rounded`}>
                          <RulerIcon className="w-3 h-3" />
                        </div>
                        <span className="font-medium text-gray-900">{item.tamanhoUL}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right font-semibold text-gray-900">
                      {item.count.toLocaleString('pt-BR')}
                    </td>
                    <td className="px-3 py-2.5 text-right text-gray-600">
                      {pct.toFixed(1)}%
                    </td>
                    <td className="px-3 py-2.5">
                      <div className={`w-full ${color.barBg} rounded-full h-1.5`}>
                        <div
                          className={`${color.barFill} h-1.5 rounded-full transition-all duration-500`}
                          style={{ width: `${Math.min(pct, 100)}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {activeFilter === 'invalid' && errorSummary.length > 0 && (
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50 sticky top-0 z-10 shadow-[0_1px_0_0_theme(colors.gray.200)]">
              <tr>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-500 uppercase tracking-wider">
                  Motivo
                </th>
                <th className="px-3 py-2.5 text-right font-semibold text-gray-500 uppercase tracking-wider">
                  Quantidade
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {errorSummary.map((item) => (
                <tr key={item.reason} className="bg-red-50/50 hover:bg-red-50 transition-colors">
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                      <span className="text-red-700">{item.reason}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-right font-semibold text-red-700">
                    {item.count.toLocaleString('pt-BR')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {activeFilter === 'all' && errorSummary.length > 0 && (
          <div className="border-t border-gray-200">
            <div className="px-3 py-2 bg-red-50/50">
              <p className="text-xs font-semibold text-red-700 uppercase tracking-wider">
                Erros encontrados
              </p>
            </div>
            <table className="min-w-full text-xs">
              <tbody className="divide-y divide-gray-100">
                {errorSummary.map((item) => (
                  <tr key={item.reason} className="bg-red-50/30 hover:bg-red-50 transition-colors">
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                        <span className="text-red-700">{item.reason}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right font-semibold text-red-700">
                      {item.count.toLocaleString('pt-BR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {((activeFilter === 'valid' && tamanhoSummary.length === 0) ||
          (activeFilter === 'invalid' && errorSummary.length === 0) ||
          (activeFilter === 'all' && totalRows === 0)) && (
          <div className="px-3 py-8 text-center text-gray-400 text-sm">
            Nenhum item nesta categoria
          </div>
        )}
      </div>
    </div>
  );
};
